import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { supabaseData } from './supabaseData';
import { Plus, Trash2, RotateCcw, DollarSign, TrendingUp, TrendingDown, Lock, Unlock, Filter, Calendar } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'parts', label: 'Parts/Materials' },
  { value: 'labor', label: 'Labor' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'office', label: 'Office' },
  { value: 'other', label: 'Other' }
];

const Money = ({ companyId }) => {
  const { colors } = useTheme();

  // LocalStorage keys for quick daily tracker (balances + income)
  const getStorageKey = () => `workvan_money_${companyId}`;
  const getInitializedKey = () => `workvan_money_initialized_${companyId}`;

  const getDefaultData = () => ({
    balances: [
      { name: 'Cash', amount: '', locked: true },
      { name: 'Checking', amount: '', locked: true }
    ],
    income: [{ name: '', amount: '', locked: false }]
  });

  const loadSavedData = () => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      const initialized = localStorage.getItem(getInitializedKey());

      if (saved) {
        const data = JSON.parse(saved);
        if (data.startingBalance !== undefined && !data.balances) {
          return {
            balances: [{ name: 'Cash', amount: data.startingBalance, locked: false }],
            income: (data.income || [{ name: '', amount: '' }]).map(i => ({ ...i, locked: i.locked || false }))
          };
        }
        return {
          balances: (data.balances || []).map(b => ({ ...b, locked: b.locked || false })),
          income: (data.income || []).map(i => ({ ...i, locked: i.locked || false }))
        };
      }

      if (!initialized) {
        localStorage.setItem(getInitializedKey(), 'true');
        return getDefaultData();
      }
    } catch (e) {}

    return {
      balances: [{ name: '', amount: '', locked: false }],
      income: [{ name: '', amount: '', locked: false }]
    };
  };

  const [data, setData] = useState(loadSavedData);

  // DB-backed expenses
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('month'); // 'week', 'month', 'year', 'all'
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newExpense, setNewExpense] = useState({
    amount: '',
    category: 'other',
    description: '',
    expense_date: new Date().toISOString().split('T')[0]
  });

  // Auto-save localStorage data
  useEffect(() => {
    localStorage.setItem(getStorageKey(), JSON.stringify(data));
  }, [data, companyId]);

  // Load expenses from DB
  const loadExpenses = useCallback(async () => {
    if (!companyId) return;
    setLoadingExpenses(true);
    try {
      let query = supabaseData
        .from('expenses')
        .select('*')
        .eq('company_id', companyId)
        .order('expense_date', { ascending: false });

      // Date filter
      const now = new Date();
      if (dateFilter === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        query = query.gte('expense_date', weekAgo.toISOString().split('T')[0]);
      } else if (dateFilter === 'month') {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        query = query.gte('expense_date', monthStart.toISOString().split('T')[0]);
      } else if (dateFilter === 'year') {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        query = query.gte('expense_date', yearStart.toISOString().split('T')[0]);
      }

      // Category filter
      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      const { data: expenseData, error } = await query;
      if (error) throw error;
      setExpenses(expenseData || []);
    } catch (err) {
      console.error('Error loading expenses:', err);
    } finally {
      setLoadingExpenses(false);
    }
  }, [companyId, dateFilter, categoryFilter]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  // Add expense to DB
  const addExpense = async () => {
    if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) return;
    try {
      const { error } = await supabaseData
        .from('expenses')
        .insert({
          company_id: companyId,
          amount: parseFloat(newExpense.amount),
          category: newExpense.category,
          description: newExpense.description || null,
          expense_date: newExpense.expense_date
        });
      if (error) throw error;
      setNewExpense({
        amount: '',
        category: 'other',
        description: '',
        expense_date: new Date().toISOString().split('T')[0]
      });
      setShowAddExpense(false);
      loadExpenses();
    } catch (err) {
      console.error('Error adding expense:', err);
    }
  };

  // Delete expense from DB
  const deleteExpense = async (id) => {
    try {
      const { error } = await supabaseData
        .from('expenses')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setExpenses(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Error deleting expense:', err);
    }
  };

  // Calculate totals
  const totalBalances = data.balances.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const totalIncome = data.income.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const totalExpenses = expenses.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const currentBalance = totalBalances + totalIncome - totalExpenses;

  // Category totals
  const categoryTotals = expenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + parseFloat(exp.amount);
    return acc;
  }, {});

  // Handlers for localStorage sections
  const updateBalanceItem = (index, field, value) => {
    setData(prev => {
      const newBalances = [...prev.balances];
      newBalances[index] = { ...newBalances[index], [field]: value };
      return { ...prev, balances: newBalances };
    });
  };

  const addBalanceRow = () => {
    setData(prev => ({
      ...prev,
      balances: [...prev.balances, { name: '', amount: '', locked: false }]
    }));
  };

  const removeBalanceRow = (index) => {
    if (data.balances[index]?.locked) return;
    setData(prev => ({
      ...prev,
      balances: prev.balances.length > 1
        ? prev.balances.filter((_, i) => i !== index)
        : [{ name: '', amount: '', locked: false }]
    }));
  };

  const toggleBalanceLock = (index) => {
    setData(prev => {
      const newBalances = [...prev.balances];
      newBalances[index] = { ...newBalances[index], locked: !newBalances[index].locked };
      return { ...prev, balances: newBalances };
    });
  };

  const updateIncomeItem = (index, field, value) => {
    setData(prev => {
      const newIncome = [...prev.income];
      newIncome[index] = { ...newIncome[index], [field]: value };
      return { ...prev, income: newIncome };
    });
  };

  const addIncomeRow = () => {
    setData(prev => ({
      ...prev,
      income: [...prev.income, { name: '', amount: '', locked: false }]
    }));
  };

  const removeIncomeRow = (index) => {
    if (data.income[index]?.locked) return;
    setData(prev => ({
      ...prev,
      income: prev.income.length > 1
        ? prev.income.filter((_, i) => i !== index)
        : [{ name: '', amount: '', locked: false }]
    }));
  };

  const toggleIncomeLock = (index) => {
    setData(prev => {
      const newIncome = [...prev.income];
      newIncome[index] = { ...newIncome[index], locked: !newIncome[index].locked };
      return { ...prev, income: newIncome };
    });
  };

  const clearAll = () => {
    if (window.confirm('Clear all balance and income amounts? Locked rows will be kept. DB expenses are not affected.')) {
      setData(prev => {
        const newBalances = prev.balances.map(b => ({ ...b, amount: '' }));
        const lockedIncome = prev.income.filter(i => i.locked).map(i => ({ ...i, amount: '' }));
        const newIncome = lockedIncome.length > 0
          ? lockedIncome
          : [{ name: '', amount: '', locked: false }];
        return { balances: newBalances, income: newIncome };
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getCategoryLabel = (value) => {
    return EXPENSE_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      fuel: '#f39c12',
      parts: '#3498db',
      labor: '#9b59b6',
      marketing: '#1abc9c',
      insurance: '#e67e22',
      vehicle: '#2ecc71',
      office: '#95a5a6',
      other: '#7f8c8d'
    };
    return categoryColors[category] || '#7f8c8d';
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.text,
    fontSize: '1rem',
    outline: 'none'
  };

  const amountInputStyle = {
    ...inputStyle,
    textAlign: 'right',
    fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace"
  };

  return (
    <div style={{
      maxWidth: '600px',
      margin: '0 auto',
      padding: '1rem'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
      }}>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: colors.text,
          margin: 0
        }}>
          Money Tracker
        </h1>
        <button
          onClick={clearAll}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            color: colors.textMuted,
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          <RotateCcw size={16} />
          Clear All
        </button>
      </div>

      {/* Current Balance Display */}
      <div style={{
        background: currentBalance >= 0
          ? 'linear-gradient(135deg, rgba(78, 204, 163, 0.15), rgba(78, 204, 163, 0.05))'
          : 'linear-gradient(135deg, rgba(231, 76, 60, 0.15), rgba(231, 76, 60, 0.05))',
        borderRadius: '16px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        border: `1px solid ${currentBalance >= 0 ? 'rgba(78, 204, 163, 0.3)' : 'rgba(231, 76, 60, 0.3)'}`
      }}>
        <div style={{
          color: colors.textMuted,
          fontSize: '0.85rem',
          marginBottom: '0.5rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Current Balance
        </div>
        <div style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: currentBalance >= 0 ? '#4ecca3' : '#e74c3c',
          fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace"
        }}>
          {formatCurrency(currentBalance)}
        </div>
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          marginTop: '1rem',
          fontSize: '0.9rem'
        }}>
          <div>
            <span style={{ color: colors.textMuted }}>Balances: </span>
            <span style={{ color: colors.text }}>{formatCurrency(totalBalances)}</span>
          </div>
          <div>
            <span style={{ color: colors.textMuted }}>In: </span>
            <span style={{ color: '#4ecca3' }}>+{formatCurrency(totalIncome)}</span>
          </div>
          <div>
            <span style={{ color: colors.textMuted }}>Out: </span>
            <span style={{ color: '#e74c3c' }}>-{formatCurrency(totalExpenses)}</span>
          </div>
        </div>
      </div>

      {/* Balances Section */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: colors.text,
            fontWeight: '600'
          }}>
            <DollarSign size={18} />
            Balances
          </div>
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: colors.text,
            fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace"
          }}>
            {formatCurrency(totalBalances)}
          </div>
        </div>

        {data.balances.map((item, index) => (
          <div key={index} style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            alignItems: 'center'
          }}>
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateBalanceItem(index, 'name', e.target.value)}
              placeholder="Account name (Cash, Checking...)"
              style={{ ...inputStyle, flex: 2 }}
            />
            <input
              type="number"
              step="0.01"
              value={item.amount}
              onChange={(e) => updateBalanceItem(index, 'amount', e.target.value)}
              placeholder="0.00"
              style={{ ...amountInputStyle, flex: 1 }}
            />
            <button
              onClick={() => toggleBalanceLock(index)}
              title={item.locked ? 'Unlock row' : 'Lock row (keeps name on clear)'}
              style={{
                padding: '0.5rem',
                background: item.locked ? 'rgba(78, 204, 163, 0.2)' : 'transparent',
                border: 'none',
                color: item.locked ? '#4ecca3' : colors.textMuted,
                cursor: 'pointer',
                borderRadius: '6px'
              }}
            >
              {item.locked ? <Lock size={16} /> : <Unlock size={16} />}
            </button>
            <button
              onClick={() => removeBalanceRow(index)}
              disabled={item.locked}
              style={{
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: item.locked ? colors.border : colors.textMuted,
                cursor: item.locked ? 'not-allowed' : 'pointer',
                borderRadius: '6px',
                opacity: item.locked ? 0.4 : 1
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}

        <button
          onClick={addBalanceRow}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.75rem',
            background: 'rgba(78, 204, 163, 0.1)',
            border: '1px dashed rgba(78, 204, 163, 0.4)',
            borderRadius: '8px',
            color: '#4ecca3',
            cursor: 'pointer',
            marginTop: '0.5rem',
            fontSize: '0.9rem'
          }}
        >
          <Plus size={18} />
          Add Account
        </button>
      </div>

      {/* Income Section */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#4ecca3',
            fontWeight: '600'
          }}>
            <TrendingUp size={18} />
            Income
          </div>
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#4ecca3',
            fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace"
          }}>
            +{formatCurrency(totalIncome)}
          </div>
        </div>

        {data.income.map((item, index) => (
          <div key={index} style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
            alignItems: 'center'
          }}>
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateIncomeItem(index, 'name', e.target.value)}
              placeholder="Description"
              style={{ ...inputStyle, flex: 2 }}
            />
            <input
              type="number"
              step="0.01"
              value={item.amount}
              onChange={(e) => updateIncomeItem(index, 'amount', e.target.value)}
              placeholder="0.00"
              style={{ ...amountInputStyle, flex: 1 }}
            />
            <button
              onClick={() => toggleIncomeLock(index)}
              title={item.locked ? 'Unlock row' : 'Lock row (keeps on clear)'}
              style={{
                padding: '0.5rem',
                background: item.locked ? 'rgba(78, 204, 163, 0.2)' : 'transparent',
                border: 'none',
                color: item.locked ? '#4ecca3' : colors.textMuted,
                cursor: 'pointer',
                borderRadius: '6px'
              }}
            >
              {item.locked ? <Lock size={16} /> : <Unlock size={16} />}
            </button>
            <button
              onClick={() => removeIncomeRow(index)}
              disabled={item.locked}
              style={{
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: item.locked ? colors.border : colors.textMuted,
                cursor: item.locked ? 'not-allowed' : 'pointer',
                borderRadius: '6px',
                opacity: item.locked ? 0.4 : 1
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}

        <button
          onClick={addIncomeRow}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.75rem',
            background: 'rgba(78, 204, 163, 0.1)',
            border: '1px dashed rgba(78, 204, 163, 0.4)',
            borderRadius: '8px',
            color: '#4ecca3',
            cursor: 'pointer',
            marginTop: '0.5rem',
            fontSize: '0.9rem'
          }}
        >
          <Plus size={18} />
          Add Income
        </button>
      </div>

      {/* Expenses Section (DB-backed) */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#e74c3c',
            fontWeight: '600'
          }}>
            <TrendingDown size={18} />
            Expenses
          </div>
          <div style={{
            fontSize: '1.1rem',
            fontWeight: '700',
            color: '#e74c3c',
            fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace"
          }}>
            -{formatCurrency(totalExpenses)}
          </div>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Calendar size={14} style={{ color: colors.textMuted }} />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                padding: '0.4rem 0.6rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                color: colors.text,
                fontSize: '0.8rem'
              }}
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Filter size={14} style={{ color: colors.textMuted }} />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                padding: '0.4rem 0.6rem',
                background: colors.bgInput,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                color: colors.text,
                fontSize: '0.8rem'
              }}
            >
              <option value="all">All Categories</option>
              {EXPENSE_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Category breakdown */}
        {Object.keys(categoryTotals).length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginBottom: '0.75rem'
          }}>
            {Object.entries(categoryTotals)
              .sort(([,a], [,b]) => b - a)
              .map(([cat, total]) => (
                <div key={cat} style={{
                  padding: '0.25rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  background: `${getCategoryColor(cat)}20`,
                  color: getCategoryColor(cat),
                  border: `1px solid ${getCategoryColor(cat)}40`
                }}>
                  {getCategoryLabel(cat)}: {formatCurrency(total)}
                </div>
              ))}
          </div>
        )}

        {/* Add Expense Form */}
        {showAddExpense ? (
          <div style={{
            background: colors.bg,
            borderRadius: '8px',
            padding: '0.75rem',
            marginBottom: '0.75rem',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newExpense.amount}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.includes('-')) return;
                  setNewExpense(prev => ({ ...prev, amount: val }));
                }}
                onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                placeholder="Amount"
                style={{ ...amountInputStyle, flex: 1 }}
                autoFocus
              />
              <select
                value={newExpense.category}
                onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
                style={{
                  ...inputStyle,
                  flex: 1,
                  cursor: 'pointer'
                }}
              >
                {EXPENSE_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input
                type="text"
                value={newExpense.description}
                onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
                style={{ ...inputStyle, flex: 2 }}
              />
              <input
                type="date"
                value={newExpense.expense_date}
                onChange={(e) => setNewExpense(prev => ({ ...prev, expense_date: e.target.value }))}
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={addExpense}
                disabled={!newExpense.amount || parseFloat(newExpense.amount) <= 0}
                style={{
                  flex: 1,
                  padding: '0.6rem',
                  background: '#e74c3c',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: (!newExpense.amount || parseFloat(newExpense.amount) <= 0) ? 0.5 : 1
                }}
              >
                Add Expense
              </button>
              <button
                onClick={() => setShowAddExpense(false)}
                style={{
                  padding: '0.6rem 1rem',
                  background: 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.textMuted,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        {/* Expense list */}
        {loadingExpenses ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            Loading expenses...
          </div>
        ) : expenses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: colors.textMuted, fontSize: '0.9rem' }}>
            No expenses recorded{dateFilter !== 'all' ? ' for this period' : ''}
          </div>
        ) : (
          expenses.map(exp => (
            <div key={exp.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0',
              borderBottom: `1px solid ${colors.border}22`
            }}>
              <div style={{
                width: '4px',
                height: '32px',
                borderRadius: '2px',
                background: getCategoryColor(exp.category)
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.9rem',
                  color: colors.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {exp.description || getCategoryLabel(exp.category)}
                </div>
                <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
                  {getCategoryLabel(exp.category)} &middot; {new Date(exp.expense_date + 'T00:00:00').toLocaleDateString()}
                </div>
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace",
                fontSize: '0.9rem',
                color: '#e74c3c',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                -{formatCurrency(exp.amount)}
              </div>
              <button
                onClick={() => deleteExpense(exp.id)}
                style={{
                  padding: '0.4rem',
                  background: 'transparent',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  opacity: 0.6
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}

        <button
          onClick={() => setShowAddExpense(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '0.75rem',
            background: 'rgba(231, 76, 60, 0.1)',
            border: '1px dashed rgba(231, 76, 60, 0.4)',
            borderRadius: '8px',
            color: '#e74c3c',
            cursor: 'pointer',
            marginTop: '0.5rem',
            fontSize: '0.9rem'
          }}
        >
          <Plus size={18} />
          Add Expense
        </button>
      </div>

      {/* Summary Card */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: colors.text }}>
          Summary
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          fontFamily: "'JetBrains Mono', 'SF Mono', Monaco, monospace",
          fontSize: '0.95rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: colors.textMuted }}>Total Balances</span>
            <span style={{ color: colors.text }}>{formatCurrency(totalBalances)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: colors.textMuted }}>Total Income</span>
            <span style={{ color: '#4ecca3' }}>+{formatCurrency(totalIncome)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: colors.textMuted }}>Total Expenses</span>
            <span style={{ color: '#e74c3c' }}>-{formatCurrency(totalExpenses)}</span>
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingTop: '0.5rem',
            marginTop: '0.5rem',
            borderTop: `1px solid ${colors.border}`
          }}>
            <span style={{ color: colors.text, fontWeight: '600' }}>Current Balance</span>
            <span style={{
              color: currentBalance >= 0 ? '#4ecca3' : '#e74c3c',
              fontWeight: '700'
            }}>
              {formatCurrency(currentBalance)}
            </span>
          </div>
        </div>
      </div>

      {/* Auto-save indicator */}
      <div style={{
        textAlign: 'center',
        marginTop: '1rem',
        fontSize: '0.8rem',
        color: colors.textMuted
      }}>
        Balances &amp; income save locally. Expenses save to your account.
      </div>
    </div>
  );
};

export default Money;
